/**
 * Sentry noise filters for third-party scripts and browser extensions.
 *
 * These were burying real errors. In a 14-day window the three highest-volume
 * "errors" in tenant-admin-prod were all code we do not control:
 *
 *  - TENANT-ADMIN-PROD-75 (193 events / 153 users) — a browser extension
 *    monkey-patches `HTMLDivElement.appendChild` and then JSON.stringify's a
 *    React fiber. Every frame is `<anonymous>`; no tenant-admin file appears.
 *  - TENANT-ADMIN-PROD-70 (170 events / 101 users) — thrown inside
 *    `/analytics.js`. The app's own request on that page succeeded (200).
 *
 * The intent is to drop errors we could not act on, never to hide our own.
 * Every rule below is narrow enough that an application bug of the same shape
 * still reports.
 */

/** Scripts we load but do not author. */
export const THIRD_PARTY_SCRIPT_URLS: RegExp[] = [
  // Vexo/Segment loader. PROD-70's frames carry the literal relative path
  // '/analytics.js' — anchor to that so a first-party file that happens to be
  // named analytics.js (e.g. https://admin.gravywork.com/analytics.js) is NOT
  // silently denied; third-party absolute URLs are covered by the host
  // patterns below. Especially important now Vexo runs ungated (#2531): this
  // list must never be able to hide our own code's errors.
  /^\/analytics\.js(\?|$)/i,
  /google-analytics\.com/i,
  /googletagmanager\.com/i,
  /\bgoogle\.com\/(ccm|rmkt)\//i,
  /clarity\.ms/i,
  /api\.vexo\.co/i,
  /js\.hs-scripts\.com/i, // HubSpot
  /js\.hsforms\.net/i,
  /connect\.facebook\.net/i,
];

/** Anything injected by an extension rather than served by us. */
export const EXTENSION_URLS: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /^chrome:\/\//i,
];

export const SENTRY_DENY_URLS: RegExp[] = [
  ...THIRD_PARTY_SCRIPT_URLS,
  ...EXTENSION_URLS,
];

export const SENTRY_IGNORE_ERRORS: (RegExp | string)[] = [
  // PROD-75. An extension patched a DOM method and serialised a React fiber.
  // Keyed on `__reactFiber` as well as the message so a genuine circular-JSON
  // bug in our own code is still reported.
  /Converting circular structure to JSON[\s\S]*__reactFiber/,
  // Benign and unactionable; fires on any resize-observing UI.
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
];
